import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePrefs } from "@/contexts/PrefsContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Download, Wallet, TrendingUp, TrendingDown, PiggyBank, Sparkles,
    Loader2, Repeat, Info,
} from "lucide-react";
import CashFlowSankey from "@/components/CashFlowSankey";
import { CATEGORY_COLOURS } from "@/lib/sankey_categories";

const PRESETS = [
    { id: "this_month",    label: "This month" },
    { id: "last_month",    label: "Last month" },
    { id: "last_3_months", label: "Last 3 months" },
    { id: "last_6_months", label: "Last 6 months" },
    { id: "this_year",     label: "This year" },
    { id: "last_year",     label: "Last year" },
    { id: "last_90",       label: "Last 90 days" },
    { id: "ytd",           label: "Year to date" },
];

const MEMBERS = [
    { id: "all",       label: "All records" },
    { id: "together",  label: "Household · Together" },
    { id: "mine",      label: "Mine" },
    { id: "partner",   label: "Partner" },
    { id: "joint",     label: "Joint" },
];

export default function CashFlowReport() {
    const [preset, setPreset]     = useState("this_month");
    const [xferOn, setXferOn]     = useState(false);
    const [member, setMember]     = useState("all");
    const [data,   setData]       = useState(null);
    const [busy,   setBusy]       = useState(false);
    const [tab,    setTab]        = useState("cashflow");
    const prefs = usePrefs();
    const hideValues = !!prefs?.hideBalances;
    const nav = useNavigate();

    useEffect(() => {
        let cancelled = false;
        setBusy(true);
        (async () => {
            try {
                const r = await api.get("/cash-flow-sankey", {
                    params: { preset, include_transfers: xferOn,
                               member: member === "all" ? undefined : member },
                });
                if (!cancelled) setData(r.data);
            } catch {
                if (!cancelled) toast.error("Failed to load cash flow");
            } finally {
                if (!cancelled) setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, [preset, xferOn, member]);

    const kpis = data?.kpis || { total_income: 0, total_expense: 0, net: 0, savings_rate: 0 };
    const empty = !data || data.empty;

    const exportPng = () => {
        const svg = document.querySelector('[data-testid="cash-flow-sankey"]');
        if (!svg) { toast.error("Chart not ready"); return; }
        const s = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([s], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `cash-flow-${data.range?.start}.svg`;
        a.click(); URL.revokeObjectURL(url);
    };

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-6" data-testid="cash-flow-report">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Reports</div>
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Cash Flow</h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-2xl">
                        See exactly where money came from, where it went, and what was saved.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={preset} onValueChange={setPreset}>
                        <SelectTrigger className="w-40 h-9" data-testid="cf-preset"><SelectValue /></SelectTrigger>
                        <SelectContent>{PRESETS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={member} onValueChange={setMember}>
                        <SelectTrigger className="w-44 h-9" data-testid="cf-member"><SelectValue /></SelectTrigger>
                        <SelectContent>{MEMBERS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button variant={xferOn ? "default" : "outline"} size="sm"
                        onClick={() => setXferOn((v) => !v)} data-testid="cf-transfers">
                        <Repeat size={12} className="mr-1" />
                        {xferOn ? "Transfers included" : "Exclude transfers"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportPng} disabled={empty} data-testid="cf-export">
                        <Download size={12} className="mr-1" /> Export SVG
                    </Button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="cf-kpis">
                <KpiCard label="Total Income" value={kpis.total_income} Icon={TrendingUp}
                    tone="emerald" hide={hideValues} />
                <KpiCard label="Total Expenses" value={kpis.total_expense} Icon={TrendingDown}
                    tone="rose" hide={hideValues} />
                <KpiCard label="Net Surplus" value={kpis.net} Icon={Wallet}
                    tone={kpis.net >= 0 ? "emerald" : "rose"} hide={hideValues} />
                <KpiCard label="Savings Rate" value={`${kpis.savings_rate}%`} Icon={PiggyBank}
                    tone="indigo" hide={hideValues} raw />
            </div>

            {/* Tabs */}
            <Tabs value={tab} onValueChange={setTab} data-testid="cf-tabs">
                <TabsList>
                    <TabsTrigger value="cashflow" data-testid="cf-tab-flow">Cash Flow</TabsTrigger>
                    <TabsTrigger value="spending" data-testid="cf-tab-spend">Spending</TabsTrigger>
                    <TabsTrigger value="income"   data-testid="cf-tab-income">Income</TabsTrigger>
                </TabsList>

                <TabsContent value="cashflow" className="mt-4">
                    <div className="rounded-2xl border border-stone-200 bg-white p-3 md:p-6 relative min-h-[520px]" data-testid="cf-sankey-panel">
                        {busy && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
                                <Loader2 size={16} className="animate-spin" />
                            </div>
                        )}
                        {empty ? (
                            <EmptyState nav={nav} />
                        ) : (
                            <>
                                <CashFlowSankey data={data} hideValues={hideValues} height={520} />
                                <Legend />
                            </>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="spending" className="mt-4">
                    <BarBreakdown links={data?.links} nodes={data?.nodes} kind="expense" hideValues={hideValues} />
                </TabsContent>

                <TabsContent value="income" className="mt-4">
                    <BarBreakdown links={data?.links} nodes={data?.nodes} kind="income" hideValues={hideValues} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function KpiCard({ label, value, Icon, tone = "stone", hide, raw }) {
    const toneCls = {
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        rose:    "bg-rose-50 text-rose-700 border-rose-200",
        indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
        stone:   "bg-white text-stone-900 border-stone-200",
    }[tone];
    const display = raw
        ? value
        : (hide ? "•••" : formatMoney(value));
    return (
        <div className={`rounded-xl border p-4 ${toneCls}`}>
            <div className="flex items-center gap-2 opacity-70">
                <Icon size={12} />
                <span className="text-[10px] uppercase tracking-wide">{label}</span>
            </div>
            <div className="text-2xl font-bold mt-1">{display}</div>
        </div>
    );
}

function formatMoney(v) {
    if (!v && v !== 0) return "—";
    const currency = "₹";
    const n = Number(v);
    if (Math.abs(n) >= 10_000_000) return `${currency}${(n / 10_000_000).toFixed(2)} Cr`;
    if (Math.abs(n) >= 100_000)    return `${currency}${(n / 100_000).toFixed(2)} L`;
    if (Math.abs(n) >= 1_000)      return `${currency}${(n / 1_000).toFixed(1)}k`;
    return `${currency}${Math.round(n)}`;
}

function EmptyState({ nav }) {
    return (
        <div className="p-8 md:p-12 text-center" data-testid="cf-empty">
            <div className="mx-auto w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
                <Sparkles size={20} className="text-indigo-600" />
            </div>
            <div className="text-lg font-semibold">Not enough activity yet</div>
            <p className="text-sm text-stone-500 mt-1 max-w-md mx-auto">
                Once your transactions and financial records are organised, VAULT will show how your money is flowing.
            </p>
            <div className="mt-5 flex justify-center gap-2 flex-wrap">
                <Button size="sm" onClick={() => nav("/quick-entry")} data-testid="cf-cta-add">Add Records</Button>
                <Button size="sm" variant="outline" onClick={() => nav("/email-capture")} data-testid="cf-cta-upload">Upload to VAULT</Button>
                <Button size="sm" variant="ghost" onClick={() => nav("/demo")} data-testid="cf-cta-demo">Explore Demo</Button>
            </div>
        </div>
    );
}

function Legend() {
    const items = Object.entries(CATEGORY_COLOURS).slice(0, 10);
    return (
        <div className="flex flex-wrap gap-3 mt-4 text-[11px] text-stone-600" data-testid="cf-legend">
            {items.map(([k, c]) => (
                <span key={k} className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: c }} />
                    {k}
                </span>
            ))}
            <span className="inline-flex items-center gap-1 text-stone-400 ml-auto">
                <Info size={11} /> Hover any band for details
            </span>
        </div>
    );
}

function BarBreakdown({ nodes, links, kind, hideValues }) {
    if (!nodes || !links) {
        return <div className="rounded-2xl border border-stone-200 bg-white p-6 text-sm text-stone-500">No data.</div>;
    }
    // Sum values per non-hub node of the requested kind.
    const rows = nodes
        .filter((n) => n.kind === kind && n.tier !== "hub")
        .map((n) => ({
            name: n.name, color: n.color,
            value: links
                .filter((l) => l.source === n.id || l.target === n.id)
                .reduce((s, l) => s + l.value, 0),
        }))
        .sort((a, b) => b.value - a.value);
    const total = rows.reduce((s, r) => s + r.value, 0) || 1;
    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-4 md:p-6" data-testid={`cf-${kind}-panel`}>
            <div className="text-sm font-semibold mb-3 capitalize">{kind === "expense" ? "Spending" : "Income"} breakdown</div>
            <div className="space-y-2">
                {rows.map((r) => (
                    <div key={r.name} className="flex items-center gap-3">
                        <div className="w-40 text-xs text-stone-700 truncate">{r.name}</div>
                        <div className="flex-1 h-3 rounded-full bg-stone-100 overflow-hidden">
                            <div className="h-3 rounded-full"
                                 style={{ width: `${(r.value / total) * 100}%`, background: r.color }} />
                        </div>
                        <div className="w-24 text-right text-xs font-medium tabular-nums">
                            {hideValues ? "•••" : formatMoney(r.value)}
                        </div>
                        <div className="w-12 text-right text-xs text-stone-500">
                            {Math.round((r.value / total) * 100)}%
                        </div>
                    </div>
                ))}
                {rows.length === 0 && <div className="text-sm text-stone-500">No {kind} recorded for this period.</div>}
            </div>
        </div>
    );
}
